// Initialize Firebase before using auth
import "@/lib/firebase";

import type { User as FirebaseUser } from "firebase/auth";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import posthog from "posthog-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  UserMembershipDto,
  UserNurseryAccessDto,
  UserResponseDto,
  UserResponseDtoRoles,
} from "@/generated/model";
import {
  clearAnalyticsContext,
  setAnalyticsContext,
  trackSessionStarted,
} from "@/lib/analytics";
import { isAuthRejection } from "@/lib/auth-error";
import { fetchUserSession, type SessionUser } from "@/lib/auth-service";
import {
  claimNurseryContextForUser,
  offlineDb,
  type OfflineUserSession,
} from "@/lib/offline/db";
import { staffUsername } from "@/lib/staff-username";

/**
 * Three-Tier Access Control Model
 *
 * Tier 1: Platform Level (User Aggregate)
 * - PLATFORM_ADMIN: User.roles = ['ADMIN'] - SaaS platform administrators
 *
 * Tier 2: Organisation Level (UserMembershipDto Entity) - handled by OrganisationContext
 * - ORG_ADMIN: UserMembershipDto.role = 'ADMIN' - Organisation administrators
 * - ORG_MEMBER: UserMembershipDto.role = 'MEMBER' - Basic organisation access
 *
 * Tier 3: Nursery Level (NurseryMembership Entity) - handled by NurseryContext
 * - NURSERY_MANAGER: Full nursery management access
 * - NURSERY_DEPUTY_MANAGER: Deputy manager access
 * - NURSERY_LEADER: Leader access
 * - NURSERY_PRACTITIONER: Basic practitioner access
 */
export type UserRoleType =
  | "PLATFORM_ADMIN"
  | "ORG_ADMIN"
  | "ORG_MEMBER"
  | "NURSERY_MANAGER"
  | "NURSERY_DEPUTY_MANAGER"
  | "NURSERY_LEADER"
  | "NURSERY_PRACTITIONER"
  | "UNKNOWN";

// Extended user data for internal use - compatible with SessionUser
interface ExtendedUserData {
  id: string;
  firebaseId?: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: UserResponseDtoRoles[];
  memberships?: UserMembershipDto[];
  nurseryAccess?: UserNurseryAccessDto[];
  requiresPasswordChange?: boolean;
}

interface AuthUser {
  id: string;
  uid: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  roles: UserResponseDtoRoles[];
  isPlatformAdmin: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  sessionUser: UserResponseDto | null;
  firebaseUser: FirebaseUser | null;
  idToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  // Role detection - Tier 1: Platform Level
  userRoleType: UserRoleType;
  isPlatformAdmin: boolean;
  // Raw access data (used by OrganisationContext and NurseryContext)
  memberships: UserMembershipDto[];
  nurseryAccess: UserNurseryAccessDto[];
  // Display-friendly login identifier (username for nursery staff, email for others)
  displayLogin: string;
  // True if user needs to set their password (first login or admin reset)
  requiresPasswordChange: boolean;
  // Actions
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const OFFLINE_SESSION_KEY = "current-user";

// Self-heal after a transient session-fetch failure: retry with exponential
// backoff (2s, 4s, 8s, …, capped) so a poor-connectivity blip does not log the
// user out and the session refreshes once signal returns.
const MAX_SESSION_RETRIES = 5;
const SESSION_RETRY_BASE_MS = 2_000;
const SESSION_RETRY_MAX_MS = 30_000;

// Cache session to IndexedDB for offline access
async function cacheSession(sessionUser: UserResponseDto): Promise<void> {
  try {
    // Convert roles to string array if it's an enum or other type
    const roles = Array.isArray(sessionUser.roles)
      ? sessionUser.roles
      : sessionUser.roles
        ? [String(sessionUser.roles)]
        : [];

    // Hand the cached organisation/nursery context to whoever is signing in, wiping
    // it when it belonged to someone else. The cache rows are keyed by a fixed
    // string, so without this a second account on the same device inherits the
    // first account's active organisation and selected nursery — which let a
    // nursery manager reach group-admin-only screens.
    await claimNurseryContextForUser(String(sessionUser.id || ""));

    await offlineDb.userSession.put({
      id: OFFLINE_SESSION_KEY,
      userId: String(sessionUser.id || ""),
      firebaseId: String(sessionUser.firebaseId || sessionUser.id || ""),
      email: sessionUser.email || null,
      firstName: sessionUser.firstName || null,
      lastName: sessionUser.lastName || null,
      roles,
      memberships: sessionUser.memberships || [],
      nurseryAccess: sessionUser.nurseryAccess || [],
      cachedAt: Date.now(),
    });
  } catch (err) {
    console.error("Failed to cache session:", err);
  }
}

// Load cached session from IndexedDB
async function loadCachedSession(): Promise<OfflineUserSession | null> {
  try {
    const cached = await offlineDb.userSession.get(OFFLINE_SESSION_KEY);
    return cached || null;
  } catch (err) {
    console.error("Failed to load cached session:", err);
    return null;
  }
}

// Clear cached session from IndexedDB
async function clearCachedSession(): Promise<void> {
  try {
    await offlineDb.userSession.delete(OFFLINE_SESSION_KEY);
  } catch (err) {
    console.error("Failed to clear cached session:", err);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [sessionData, setSessionData] = useState<SessionUser | null>(null);
  const [cachedSession, setCachedSession] = useState<OfflineUserSession | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [cacheChecked, setCacheChecked] = useState(false);

  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptsRef = useRef(0);
  const refreshSessionRef = useRef<() => Promise<void>>(() =>
    Promise.resolve(),
  );

  // Load cached session on mount; refresh the session when connectivity returns.
  useEffect(() => {
    loadCachedSession().then((cached) => {
      setCachedSession(cached);
      setCacheChecked(true);
    });

    const handleOnline = () => {
      void refreshSessionRef.current();
    };
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const clearSessionRetry = useCallback(() => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const scheduleSessionRetry = useCallback(() => {
    if (retryAttemptsRef.current >= MAX_SESSION_RETRIES) return;

    const delay = Math.min(
      SESSION_RETRY_BASE_MS * 2 ** retryAttemptsRef.current,
      SESSION_RETRY_MAX_MS,
    );
    retryAttemptsRef.current += 1;
    clearSessionRetry();
    retryTimerRef.current = setTimeout(() => {
      void refreshSessionRef.current();
    }, delay);
  }, [clearSessionRetry]);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Fetch user data from API
          const session = await fetchUserSession(firebaseUser);
          // A successful fetch supersedes any retry pending from an earlier
          // transient failure.
          clearSessionRetry();
          retryAttemptsRef.current = 0;
          setSessionData(session);

          // Identify user in PostHog
          posthog.identify(String(session.id), {
            email: session.email,
            name: `${session.firstName || ""} ${session.lastName || ""}`.trim(),
          });

          // Set analytics context for behavioral tracking
          // Cast to ExtendedUserData to access memberships/nurseryAccess
          const extendedSession = session as unknown as ExtendedUserData;
          // Determine role from memberships/nurseryAccess
          let userRoleType: string | null = null;
          if (session.roles?.includes("ADMIN")) {
            userRoleType = "PLATFORM_ADMIN";
          } else if (
            extendedSession.memberships?.some(
              (m: UserMembershipDto) =>
                m.status === "ACTIVE" && m.role === "ADMIN",
            )
          ) {
            userRoleType = "ORG_ADMIN";
          } else if (extendedSession.nurseryAccess?.[0]) {
            const accessRole = extendedSession.nurseryAccess[0].role;
            userRoleType =
              accessRole === "MANAGER"
                ? "NURSERY_MANAGER"
                : accessRole === "DEPUTY_MANAGER"
                  ? "NURSERY_DEPUTY_MANAGER"
                  : accessRole === "LEADER"
                    ? "NURSERY_LEADER"
                    : accessRole === "PRACTITIONER"
                      ? "NURSERY_PRACTITIONER"
                      : null;
          }

          setAnalyticsContext({
            userId: String(session.id),
            userRoleType,
            nurseryId: extendedSession.nurseryAccess?.[0]?.nurseryId || null,
          });

          // Track session start if this is a fresh login (login_method is set by UserAuthForm)
          const loginMethod = sessionStorage.getItem("login_method") as
            | "email_password"
            | "username_password"
            | "pin"
            | null;
          if (loginMethod) {
            trackSessionStarted(loginMethod);
            sessionStorage.removeItem("login_method");
          }

          // Cache for offline access
          await cacheSession(session);
        } catch (error) {
          console.error("Failed to fetch user session:", error);

          if (isAuthRejection(error)) {
            // Token invalid/revoked — a genuine sign-out.
            clearSessionRetry();
            retryAttemptsRef.current = 0;
            setSessionData(null);
            setCachedSession(null);
            await clearCachedSession();
          } else {
            // Transient/network failure (poor connectivity) — keep the
            // existing or cached session and retry; do not log the user out.
            scheduleSessionRetry();
          }
        }
      } else {
        clearSessionRetry();
        retryAttemptsRef.current = 0;
        setSessionData(null);
        setCachedSession(null);
        posthog.reset();
        clearAnalyticsContext();
        await clearCachedSession();
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [clearSessionRetry, scheduleSessionRetry]);

  // Re-fetch the session for the current Firebase user. On a genuine auth
  // rejection (401/403) the session and cache are cleared; on a transient
  // failure the existing session is kept and a backoff retry is scheduled.
  const refreshSession = useCallback(async () => {
    const auth = getAuth();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;

    try {
      const session = await fetchUserSession(firebaseUser);
      clearSessionRetry();
      retryAttemptsRef.current = 0;
      setSessionData(session);
      await cacheSession(session);
    } catch (error) {
      console.error("Failed to refresh session:", error);

      if (isAuthRejection(error)) {
        clearSessionRetry();
        retryAttemptsRef.current = 0;
        setSessionData(null);
        setCachedSession(null);
        await clearCachedSession();
        return;
      }

      scheduleSessionRetry();
    }
  }, [clearSessionRetry, scheduleSessionRetry]);

  useEffect(() => {
    refreshSessionRef.current = refreshSession;
  }, [refreshSession]);

  // Clear any pending retry timer on unmount.
  useEffect(() => clearSessionRetry, [clearSessionRetry]);

  const value = useMemo<AuthContextValue>(() => {
    // Use the live session if available, otherwise fall back to the cached
    // session. The cache is cleared on a genuine sign-out or 401/403, so a
    // present cache means Firebase still considers the user authenticated —
    // this keeps the user logged in through a transient connectivity failure.
    const effectiveUser: UserResponseDto | null =
      (sessionData as UserResponseDto | null) ||
      (cachedSession
        ? ({
            id: cachedSession.userId,
            firebaseId: cachedSession.firebaseId,
            email: cachedSession.email,
            firstName: cachedSession.firstName,
            lastName: cachedSession.lastName,
            roles: cachedSession.roles,
            memberships: cachedSession.memberships as UserMembershipDto[],
            nurseryAccess:
              cachedSession.nurseryAccess as UserNurseryAccessDto[],
            requiresPasswordChange: false,
            createdAt: "",
            updatedAt: "",
          } as unknown as UserResponseDto)
        : null);

    const isAuthenticated = !!effectiveUser;

    let user: AuthUser | null = null;
    let memberships: UserMembershipDto[] = [];
    let nurseryAccess: UserNurseryAccessDto[] = [];
    let userRoleType: UserRoleType = "UNKNOWN";
    let isPlatformAdmin = false;

    if (effectiveUser) {
      memberships = effectiveUser.memberships || [];
      nurseryAccess = effectiveUser.nurseryAccess || [];
      const roles =
        (effectiveUser.roles as unknown as UserResponseDtoRoles[]) || [];

      // Tier 1: Platform Admin - identified by User.roles = ['ADMIN']
      isPlatformAdmin = roles.includes(UserResponseDtoRoles.ADMIN);

      user = {
        id: String(effectiveUser.id || ""),
        uid: String(effectiveUser.firebaseId || effectiveUser.id || ""),
        email: effectiveUser.email || null,
        firstName: effectiveUser.firstName || null,
        lastName: effectiveUser.lastName || null,
        roles,
        isPlatformAdmin,
      };

      // Determine user role type based on three-tier hierarchy
      if (isPlatformAdmin) {
        userRoleType = "PLATFORM_ADMIN";
      } else if (memberships.length > 0) {
        const activeMembership = memberships.find(
          (m: UserMembershipDto) => m.status === "ACTIVE",
        );
        if (activeMembership?.role === "ADMIN") {
          userRoleType = "ORG_ADMIN";
        } else if (activeMembership?.role === "MEMBER") {
          userRoleType = "ORG_MEMBER";
        }
      } else if (nurseryAccess.length > 0) {
        const access = nurseryAccess[0];
        switch (access?.role) {
          case "MANAGER":
            userRoleType = "NURSERY_MANAGER";
            break;
          case "DEPUTY_MANAGER":
            userRoleType = "NURSERY_DEPUTY_MANAGER";
            break;
          case "LEADER":
            userRoleType = "NURSERY_LEADER";
            break;
          case "PRACTITIONER":
            userRoleType = "NURSERY_PRACTITIONER";
            break;
        }
      }
    }

    const displayLogin = user?.email ? staffUsername(user.email) : "";

    const requiresPasswordChange = !!effectiveUser?.requiresPasswordChange;

    return {
      user,
      sessionUser: sessionData || null,
      firebaseUser: sessionData?.firebaseUser || null,
      idToken: sessionData?.idToken || null,
      // Stay in the loading state until the cached session has been read, so a
      // slow IndexedDB read can't briefly present an authenticated user as
      // logged out and bounce them to /login on a cold start.
      isLoading: isLoading || !cacheChecked,
      isAuthenticated,
      userRoleType,
      isPlatformAdmin,
      memberships,
      nurseryAccess,
      displayLogin,
      requiresPasswordChange,
      refreshSession,
    };
  }, [sessionData, cachedSession, cacheChecked, isLoading, refreshSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // During HMR, providers can temporarily unmount. Return loading state in dev.
    if (import.meta.env.DEV) {
      return {
        user: null,
        sessionUser: null,
        firebaseUser: null,
        idToken: null,
        isLoading: true,
        isAuthenticated: false,
        userRoleType: "UNKNOWN" as const,
        isPlatformAdmin: false,
        memberships: [],
        nurseryAccess: [],
        displayLogin: "",
        requiresPasswordChange: false,
        refreshSession: async () => {},
      };
    }
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
